/**
 * SearchEmptyState Component
 *
 * Centralized empty state for search interfaces in attack path wizards.
 * Displays consistent messaging with entity-specific icon and Clear Search action.
 *
 * Icons are automatically selected based on entityName:
 * - assets → NoAssetsFoundIcon (custom laptop icon)
 * - techniques → Target
 * - vulnerabilities → ShieldExclamationIcon
 * - icons → Shapes
 * - fallback → MagnifyingGlassIcon
 *
 * Message format: "No {EntityName} match your search"
 * (Entity name capitalized for proper nouns: Assets, Techniques, Vulnerabilities)
 *
 * Usage:
 * <SearchEmptyState
 *   searchTerm="user search"
 *   onClearSearch={() => setSearchValue('')}
 *   entityName="assets"  // → "No Assets match your search" + laptop icon
 * />
 */

import {
  MagnifyingGlassIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Shapes, Target } from 'lucide-react';
import type { ComponentType } from 'react';

import { NoAssetsFound as NoAssetsFoundIcon } from '@/components/icons';
import { NO_DATA_ICON_CLASS } from '@/components/legacy-ui/utils/createNoDataConfig';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/classname';

export interface SearchEmptyStateProps {
  /** The search term that produced no results */
  searchTerm: string;

  /** Icon component to display (defaults to MagnifyingGlassIcon) */
  icon?: ComponentType<{ className?: string; strokeWidth?: number }>;

  /** Callback when Clear Search button is clicked */
  onClearSearch: () => void;

  /** Entity name for contextual messaging (defaults to "results") */
  entityName?: string;

  /** Custom container height (defaults to h-[320px]) */
  height?: string;

  /** Test ID prefix for testing */
  testIdPrefix?: string;
}

/**
 * Capitalizes entity names according to codebase conventions.
 * - Proper nouns: Assets, Techniques, Vulnerabilities, Seeds
 * - Common nouns: jobs, integrations, icons
 */
function capitalizeEntityName(entityName: string): string {
  const properNouns = ['assets', 'techniques', 'vulnerabilities', 'seeds'];
  const normalized = entityName.toLowerCase();

  if (properNouns.includes(normalized)) {
    return entityName.charAt(0).toUpperCase() + entityName.slice(1);
  }

  return entityName.toLowerCase();
}

/**
 * Entity-to-icon mapping for consistent empty state visuals.
 * Icons match entity representations used elsewhere in the application.
 *
 * - Assets: NoAssetsFoundIcon (custom laptop icon, matches asset table)
 * - Techniques: Target (MITRE ATT&CK targeting concept)
 * - Vulnerabilities: ShieldExclamationIcon (security focus)
 * - Icons: Shapes (represents visual/geometric elements)
 */
const SEARCH_ENTITY_ICONS: Record<
  string,
  ComponentType<{ className?: string; strokeWidth?: number }>
> = {
  assets: NoAssetsFoundIcon,
  techniques: Target,
  vulnerabilities: ShieldExclamationIcon,
  icons: Shapes,
};

/**
 * Displays empty state for search interfaces with consistent styling.
 * Shows icon, contextual message with search term, and Clear Search button.
 */
export function SearchEmptyState({
  searchTerm: _searchTerm,
  icon,
  onClearSearch,
  entityName = 'results',
  height = 'h-[320px]',
  testIdPrefix = 'search-empty',
}: SearchEmptyStateProps) {
  // Resolution order: explicit prop > entity mapping > default fallback
  const Icon = icon ?? SEARCH_ENTITY_ICONS[entityName] ?? MagnifyingGlassIcon;
  return (
    <div
      className={cn(
        'border-chariot-stroke bg-chariot-background-secondary',
        'flex flex-col items-center justify-center gap-4 rounded-lg border p-4',
        height
      )}
      role="status"
      aria-live="polite"
      data-testid={testIdPrefix}
    >
      {/* Icon */}
      <Icon
        className={cn(NO_DATA_ICON_CLASS, 'mb-3 size-12')}
        strokeWidth={1.5}
        aria-hidden="true"
      />

      {/* Message */}
      <div className="text-center">
        <p className="text-chariot-text-secondary text-sm font-medium">
          No {capitalizeEntityName(entityName)} match your search
        </p>
      </div>

      {/* Clear Search Action */}
      <Button
        data-testid={`${testIdPrefix}-clear-button`}
        onClick={onClearSearch}
        size="sm"
        className="mt-3"
      >
        Clear Search
      </Button>
    </div>
  );
}
